import {
    BodyTextMedium,
    BodyTextXSmall,
    Button,
    ButtonGroup,
    HeadingText50,
    pxToRem,
} from '@modernhealth/vital-components';
import styled from '@emotion/styled';
import { Slider } from 'antd';
import { Howl } from 'howler';
import moment from 'moment';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie-player';

import loaderAnimation from 'src/public/animations/loader.json';
import pausePlayAnimation from 'src/public/animations/pause.json';
import IconBackward15 from 'src/public/img/icon-backward-15.svg';
import IconForward15 from 'src/public/img/icon-forward-15.svg';
import { color } from 'src/shared/styles/variables';
import { formatScondsToRoundedMinutes } from '../helpers/formatScondsToRoundedMinutes';
import { useAudioPlayerSaveProgress } from './hooks/useAudioPlayerSaveProgress';

const Container = styled.div({
    width: 380,
});

const InfoContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
});

// 300px
const THUMBNAIL_HEIGHT = 300;
// 300px
const THUMBNAIL_WIDTH = 300;

const InfoImage = styled.img({
    objectFit: 'cover',
    borderRadius: pxToRem(15),
});

interface latestPlayTimeInfo {
    audioTimePlayedMS: number;
    lastPlayTime: number;
}

const getTimePlayed = (
    isPlaying: boolean,
    lastPlayTime: number,
    audioTimePlayedMS: number,
): latestPlayTimeInfo => {
    const currentTime = Date.now();
    let updatedLastPlayTime = lastPlayTime;
    let updatedAudioTimePlayedMS = audioTimePlayedMS;
    if (isPlaying) {
        updatedLastPlayTime = currentTime;
    } else if (lastPlayTime !== 0) {
        const lastTimeFraction = currentTime - lastPlayTime;
        updatedAudioTimePlayedMS += lastTimeFraction;
        updatedLastPlayTime = 0;
    }
    return { audioTimePlayedMS: updatedAudioTimePlayedMS, lastPlayTime: updatedLastPlayTime };
};

const ProgressContainer = styled.div({
    marginTop: 80,
    display: 'flex',
    flexDirection: 'column',

    '.ant-slider': {
        margin: '0px 0px 9px 0px',
    },
    '.ant-slider:hover .ant-slider-track': {
        backgroundColor: color.cornflowerBlueDark,
    },
    '.ant-slider:hover .ant-slider-handle:not(.ant-tooltip-open)': {
        borderColor: color.white,
    },
    '.ant-slider-rail': {
        height: 6,
        backgroundColor: color.catskillWhite,
        borderRadius: 18,
    },
    '.ant-slider-track': {
        height: 6,
        backgroundColor: color.cornflowerBlueDark,
    },
    '.ant-slider-handle': {
        marginTop: -9,
        width: 24,
        height: 24,
        border: `6px solid ${color.white}`,
        backgroundColor: color.cornflowerBlueDark,
        boxShadow: '0px 1px 8px rgb(198 198 198 / 50%)',
    },
});

const ProgressLabelsContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: pxToRem(2),
});

const ControlsContainer = styled(ButtonGroup)({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: pxToRem(22),
    justifyContent: 'center',
});

const StyledLottie = styled(Lottie)<{ visible: boolean }>(({ visible }) => ({
    opacity: visible ? 1 : 0,
    transition: `opacity ${visible ? '.5s' : '.2s'} ease-in-out`,
}));

const PlayPause = styled(StyledLottie)({
    height: 30,
    filter: 'invert(1)',
});

const Loader = styled(StyledLottie)({
    position: 'absolute',
    height: 60,
});

/**
 * PlayPauseButton represents a user control for starting or pausing a media player
 */
const PlayPauseButton: React.FC<{
    animationProps: AnimationProps;
    onAnimationEnd: VoidFunction;
    loading: boolean;
    onClick: VoidFunction;
    title: string;
}> = ({ animationProps, loading, onAnimationEnd, onClick, title }) => {
    return (
        <Button
            aria-label={title}
            css={{
                backgroundColor: color.gray85,
                borderRadius: '50%',
                padding: pxToRem(12),
            }}
            disabled={loading}
            onClick={() => onClick()}
            variant="flat"
        >
            <Loader
                aria-hidden
                animationData={loaderAnimation}
                loop
                play={loading}
                speed={0.8}
                visible={loading}
            />
            <PlayPause
                aria-hidden
                visible={!loading}
                animationData={pausePlayAnimation}
                loop={false}
                speed={1}
                direction={1}
                onComplete={onAnimationEnd}
                {...animationProps}
            />
        </Button>
    );
};

/**
 * SeekButton represents a user control for rewinding or forwarding a media player.
 */
const SeekButton: React.FC<{
    iconUrl: string;
    loading: boolean;
    onClick: VoidFunction;
    title: string;
}> = ({ iconUrl, loading, onClick, title }) => {
    return (
        <Button
            aria-label={title}
            css={{ borderRadius: '50%', padding: pxToRem(8) }}
            disabled={loading}
            onClick={() => onClick()}
            variant="flat"
        >
            <img alt="" src={iconUrl} />
        </Button>
    );
};

export interface AudioPlayerProps {
    audioSrc: string;
    imgSrc?: string;
    title?: string;
    onPlay?: VoidFunction;
    onListened?: VoidFunction;
    onExit?: CallableFunction;
    onProgressSave: (progressPercentage: number) => Promise<void>;
}

interface AnimationProps {
    play: boolean;
    segments: [number, number];
    goTo?: number | null;
}

export const AudioPlayer: FC<AudioPlayerProps> = ({
    audioSrc,
    imgSrc,
    title,
    onPlay,
    onListened,
    onExit,
    onProgressSave,
}) => {
    const { t } = useTranslation();
    const [timeStarted, _] = useState(Date.now());
    const [audioDurationS, setAudioDurationS] = useState(0);
    const [lastPlayTimeMS, setLastPlayTimeMS] = useState(0);
    const [audioTimePlayedMS, setAudioTimePlayedMS] = useState(0);
    const [audioCurrentTimeS, setAudioCurrentTimeS] = useState(0);
    const [sliderValueS, setSliderValueS] = useState(0);
    const [maxSliderValueMS, setMaxSliderValueMS] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [animationProps, setAnimationProps] = useState<AnimationProps>({
        play: false,
        segments: [14, 28],
        goTo: 14,
    });
    const [info, setInfo] = useState({
        played: false,
        isListened: false,
    });

    const sound = useMemo(
        () =>
            new Howl({
                src: [audioSrc],
                html5: true,
                preload: true,
                onplay: () => {
                    setIsPlaying(true);
                    setLastPlayTimeMS(Date.now);
                },
                onpause: () => setIsPlaying(false),
                onend: () => {
                    setAnimationProps(props => ({
                        ...props,
                        play: true,
                        goTo: null,
                    }));
                    setIsPlaying(false);
                },
            }),
        [audioSrc],
    );
    const audioTimePlayedMSRef = React.useRef<number>(0);
    const maxSliderValueMSRef = React.useRef<number>(0);
    const audioDurationSRef = React.useRef<number>();
    const lastPlayTimeMSRef = React.useRef<number>(0);

    useAudioPlayerSaveProgress(sound, isPlaying, onProgressSave);

    sound.once('load', () => {
        const duration = sound.duration();
        setAudioDurationS(duration);
        audioDurationSRef.current = duration;
    });

    useEffect(() => {
        return () => {
            sound.unload();
        };
    }, [sound]);

    useEffect(() => {
        const interval = setInterval(() => {
            const seek = sound.seek();
            const duration = sound.duration();
            if (typeof seek === 'number') {
                // seek === 0 indicates that we've reached the end and we want to reset progress bar position
                if (isPlaying || seek === 0) {
                    setAudioCurrentTimeS(seek);
                    setSliderValueS(seek);
                }

                if (seek / duration > 0.95 && !info.isListened) {
                    setInfo(prevInfo => ({ ...prevInfo, isListened: true }));
                    onListened?.();
                }
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [sound, isPlaying, info.isListened, onListened]);

    useEffect(() => {
        if (isPlaying && !info.played) {
            setInfo(prevInfo => ({ ...prevInfo, played: true }));
            onPlay?.();
        }
    }, [info.played, onPlay, isPlaying]);

    useEffect(() => {
        const playTimeInfo = getTimePlayed(
            isPlaying,
            lastPlayTimeMSRef.current ?? 0,
            audioTimePlayedMSRef.current ?? 0,
        );
        setAudioTimePlayedMS(playTimeInfo.audioTimePlayedMS);
        setLastPlayTimeMS(playTimeInfo.lastPlayTime);
    }, [isPlaying]);

    useEffect(() => {
        if (sliderValueS > maxSliderValueMS) {
            setMaxSliderValueMS(sliderValueS);
        }
    }, [sliderValueS, maxSliderValueMS]);

    useEffect(() => {
        // Use refs to have the values on the onExit effect
        audioTimePlayedMSRef.current = audioTimePlayedMS;
        maxSliderValueMSRef.current = maxSliderValueMS;
        lastPlayTimeMSRef.current = lastPlayTimeMS;
    }, [audioTimePlayedMS, maxSliderValueMS, lastPlayTimeMS]);

    useEffect(() => {
        return () => {
            const timeOnPageS = Date.now() - timeStarted;
            const playTimeInfo = getTimePlayed(
                false,
                lastPlayTimeMSRef.current ?? 0,
                audioTimePlayedMSRef.current ?? 0,
            );
            onExit?.(
                timeOnPageS,
                playTimeInfo.audioTimePlayedMS,
                maxSliderValueMSRef.current,
                audioDurationSRef.current,
            );
        };
    }, [onExit, timeStarted]);

    const formatSecondToDisplayFormat = (seconds: number) =>
        moment.utc(seconds * 1000).format('m:ss');

    const handleChangeTime = (toSecond: number) => {
        sound.seek(toSecond);
        setSliderValueS(toSecond);
    };

    const seekBy = seconds => {
        const currentSeek = sound.seek();
        handleChangeTime(currentSeek + seconds);
    };

    const handleAnimationComplete = () => {
        setAnimationProps(props => ({
            ...props,
            play: false,
            segments: isPlaying ? [0, 14] : [14, 28],
        }));
    };

    const handlePlayPauseClick = () => {
        if (!animationProps.play) {
            setAnimationProps(props => ({
                ...props,
                play: true,
                goTo: null,
            }));
            if (isPlaying) {
                sound.pause();
            } else {
                sound.play();
            }
        }
    };

    const playerState = sound.state();

    return (
        <Container>
            <InfoContainer>
                {imgSrc && (
                    <InfoImage
                        width={THUMBNAIL_WIDTH}
                        height={THUMBNAIL_HEIGHT}
                        src={imgSrc}
                        alt=""
                    />
                )}
                {title && (
                    <HeadingText50 marginBottom={0} marginTop={50}>
                        {title}
                    </HeadingText50>
                )}
                <BodyTextMedium marginTop={10}>
                    {t('shared:audio_player.description', {
                        minutes: formatScondsToRoundedMinutes(audioDurationS),
                    })}
                </BodyTextMedium>
            </InfoContainer>
            <ProgressContainer>
                <div aria-hidden>
                    <Slider
                        min={0}
                        max={audioDurationS}
                        defaultValue={0}
                        onChange={seconds => setSliderValueS(seconds as number)}
                        tipFormatter={formatSecondToDisplayFormat}
                        value={sliderValueS}
                        onAfterChange={handleChangeTime}
                    />
                </div>
                <ProgressLabelsContainer aria-hidden>
                    <BodyTextXSmall>
                        {formatSecondToDisplayFormat(audioCurrentTimeS)}
                    </BodyTextXSmall>
                    <BodyTextXSmall>{formatSecondToDisplayFormat(audioDurationS)}</BodyTextXSmall>
                </ProgressLabelsContainer>
            </ProgressContainer>
            <ControlsContainer>
                <SeekButton
                    iconUrl={IconBackward15}
                    loading={playerState !== 'loaded'}
                    onClick={() => seekBy(-15)}
                    title={t('shared:media_player.seek_backward')}
                />

                <PlayPauseButton
                    animationProps={animationProps}
                    loading={playerState !== 'loaded'}
                    onAnimationEnd={handleAnimationComplete}
                    onClick={handlePlayPauseClick}
                    title={
                        isPlaying ? t('shared:media_player.pause') : t('shared:media_player.play')
                    }
                />
                <SeekButton
                    iconUrl={IconForward15}
                    loading={playerState !== 'loaded'}
                    onClick={() => seekBy(15)}
                    title={t('shared:media_player.seek_forward')}
                />
            </ControlsContainer>
        </Container>
    );
};
